/* eslint-disable @typescript-eslint/no-explicit-any */
"use client"

import { useState, useEffect } from "react"
import type { ChangeEvent, FormEvent, KeyboardEvent } from "react"
import { Button } from "@/components/ui/button"
import { useRouter } from "next/navigation"
import axiosInstance from "@/lib/utils/axiosInstance"
import { getSpecialties } from "@/services/preceptor-service"
import { PhoneInput as ShadcnPhoneInput } from "@/components/phone-input"
import { isValidPhoneNumber, isPossiblePhoneNumber } from "react-phone-number-input"
import { Check, ChevronsUpDown, X, CheckCircle2, AlertCircle } from "lucide-react"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import { cn } from "@/lib/utils"

type PreceptorTrack = "" | "np" | "pa" | "img"

interface PreceptorFormData {
  fullName: string
  titles: string[]
  email: string
  phoneNumber: string
  specialties: string[]
  track: PreceptorTrack
  siteName: string
  siteAddress: string
  siteLatitude: number | null
  siteLongitude: number | null
  preceptedBefore: string
  patientPopulation: string[]
  additionalNotes: string
  scheduleNotes: string
}

interface Specialty {
  id: number | string
  name: string
}

export function BecomePreceptorFormCompact() {
  const router = useRouter()
  const [formData, setFormData] = useState<PreceptorFormData>({
    fullName: "",
    titles: [],
    email: "",
    phoneNumber: "",
    specialties: [],
    track: "np",
    siteName: "",
    siteAddress: "",
    siteLatitude: null,
    siteLongitude: null,
    preceptedBefore: "",
    patientPopulation: [],
    additionalNotes: "",
    scheduleNotes: "",
  })

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [specialties, setSpecialties] = useState<Specialty[]>([])
  const [loadingSpecialties, setLoadingSpecialties] = useState(true)
  const [emailTouched, setEmailTouched] = useState(false)
  const [titlePopoverOpen, setTitlePopoverOpen] = useState(false)
  const [titleQuery, setTitleQuery] = useState("")
  const [specialtyPopoverOpen, setSpecialtyPopoverOpen] = useState(false)
  const [specialtyQuery, setSpecialtyQuery] = useState("")
  const [phoneError, setPhoneError] = useState("")

  const addTitle = (value: string) => {
    const trimmed = value.trim()

    if (!trimmed) {
      return
    }

    setFormData((prev) => {
      const alreadySelected = prev.titles.some(
        (title) => title.toLowerCase() === trimmed.toLowerCase()
      )

      if (alreadySelected || prev.titles.length >= MAX_TITLES) {
        return prev
      }

      return {
        ...prev,
        titles: [...prev.titles, trimmed],
      }
    })
  }

  const removeTitle = (value: string) => {
    const target = value.trim().toLowerCase()

    setFormData((prev) => ({
      ...prev,
      titles: prev.titles.filter((title) => title.toLowerCase() !== target),
    }))
  }

  const toggleTitle = (value: string) => {
    const trimmed = value.trim()

    if (!trimmed) {
      return
    }

    setFormData((prev) => {
      const exists = prev.titles.some((title) => title.toLowerCase() === trimmed.toLowerCase())

      if (exists) {
        return {
          ...prev,
          titles: prev.titles.filter((title) => title.toLowerCase() !== trimmed.toLowerCase()),
        }
      }

      if (prev.titles.length >= MAX_TITLES) {
        return prev
      }

      return {
        ...prev,
        titles: [...prev.titles, trimmed],
      }
    })
  }

  const handleTitleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && titleQuery.trim()) {
      event.preventDefault()
      addTitle(titleQuery)
      setTitleQuery("")
    }
  }

  const handleAddCustomTitle = (value: string) => {
    if (!value.trim()) {
      return
    }

    addTitle(value)
    setTitleQuery("")
  }

  // Fetch specialties on component mount
  useEffect(() => {
    const fetchSpecialties = async () => {
      try {
        const data = await getSpecialties()
        setSpecialties(data || [])
      } catch (err) {
        console.error('Failed to fetch specialties:', err)
        setSpecialties([])
      } finally {
        setLoadingSpecialties(false)
      }
    }

    fetchSpecialties()
  }, [])

  useEffect(() => {
    if (!titlePopoverOpen) {
      setTitleQuery("")
    }
  }, [titlePopoverOpen])

  useEffect(() => {
    if (!specialtyPopoverOpen) {
      setSpecialtyQuery("")
    }
  }, [specialtyPopoverOpen])

  const TITLE_OPTIONS = [
    { value: "DO", label: "DO - Doctor of Osteopathic Medicine" },
    { value: "MD", label: "MD - Medical Doctor" },
    { value: "PhD", label: "PhD - Doctor of Philosophy" },
    { value: "PMHNP", label: "PMHNP - Psychiatric Mental Health Nurse Practitioner" },
    { value: "FNP", label: "FNP - Family Nurse Practitioner" },
    { value: "AGACNP", label: "AGACNP - Adult Gerontology Acute Care Nurse Practitioner" },
    { value: "AGPCNP", label: "AGPCNP - Adult Gerontology Primary Care Nurse Practitioner" },
    { value: "AGNP", label: "AGNP - Adult Gerontology Nurse Practitioner" },
    { value: "PNP", label: "PNP - Pediatric Nurse Practitioner" },
    { value: "WHNP", label: "WHNP - Women's Health Nurse Practitioner" },
    { value: "PA", label: "PA - Physician Assistant" },
  ] as const

  const MAX_TITLES = 10

  const PATIENT_POPULATIONS = [
    "Pediatrics (0-12 years)",
    "Adolescents (13-18 years)",
    "Adults (19-65 years)",
    "Geriatric (65+ years)",
    "Mixed Ages",
  ]

  const isEmailFormatValid = (value: string) => {
    if (!value) return false
    // Basic RFC 5322 compliant email check without external dependency
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
  }

  const getEmailValidationMessage = (value: string) => {
    const trimmedEmail = value.trim()

    if (!trimmedEmail) {
      return "Email is required."
    }

    if (!isEmailFormatValid(trimmedEmail)) {
      return "Please enter a valid email address."
    }

    return ""
  }

  // Phone validation utilities
  const validatePhoneNumber = (phone: string): boolean => {
    if (!phone) return false
    try {
      return isValidPhoneNumber(phone)
    } catch (err) {
      console.error("Phone validation error", err)
      return false
    }
  }

  const isPossiblePhone = (phone: string): boolean => {
    if (!phone) return false
    try {
      return isPossiblePhoneNumber(phone)
    } catch (err) {
      console.error("Phone validation error", err)
      return false
    }
  }

  const getPhoneValidationMessage = (phone: string): string => {
    if (!phone) return ""
    if (!isPossiblePhone(phone)) {
      return "Phone number appears incomplete. Please check the format."
    }
    if (!validatePhoneNumber(phone)) {
      return "This doesn't appear to be a valid phone number for the selected country."
    }
    return ""
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handlePhoneChange = (value: string | undefined) => {
    const phoneValue = value ?? ""
    setFormData((prev) => ({
      ...prev,
      phoneNumber: phoneValue,
    }))

    // Update validation message
    if (phoneValue) {
      const validationMessage = getPhoneValidationMessage(phoneValue)
      setPhoneError(validationMessage)
    } else {
      setPhoneError("")
    }
  }

  const handleCheckboxChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = e.target
    setFormData((prev) => ({
      ...prev,
      patientPopulation: checked
        ? [...prev.patientPopulation, value]
        : prev.patientPopulation.filter((p) => p !== value),
    }))
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setEmailTouched(true)

    const emailValidationMessage = getEmailValidationMessage(formData.email)
    if (emailValidationMessage) {
      setError(emailValidationMessage)
      return
    }

    // Validate phone number
    if (!validatePhoneNumber(formData.phoneNumber)) {
      const phoneErrorMsg = "Please enter a valid phone number (include country code if needed)."
      setError(phoneErrorMsg)
      setLoading(false)
      return
    }

    setLoading(true)

    try {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { scheduleNotes, ...formRest } = formData
      const payload: Record<string, unknown> = {
        ...formRest,
        specialty: formData.specialties.join(", "),
        schedule: formData.scheduleNotes.trim() || null,
      }

      const response = await axiosInstance.post("/preceptor-applications/compact-intake", payload)

      if (response.data) {
        setSuccess(true)
        setTimeout(() => {
          router.push(`/auth/preceptor/application-submitted?track=${formData.track}&mode=admin-email-only`)
        }, 2000)
      }
    } catch (err: any) {
      const validationErrors = err.response?.data?.errors
      const firstFieldError = validationErrors
        ? Object.values(validationErrors).find((messages) => Array.isArray(messages) && messages.length > 0)
        : null

      setError(
        (Array.isArray(firstFieldError) ? firstFieldError[0] : null) ||
          err.response?.data?.message ||
          err.message ||
          "Failed to submit application. Please try again."
      )
    } finally {
      setLoading(false)
    }
  }

  const emailValidationMessage = getEmailValidationMessage(formData.email)
  const emailMessage = emailTouched ? emailValidationMessage : ""
  const emailReady = Boolean(formData.email.trim()) && !emailValidationMessage

  const isFormComplete =
    Boolean(formData.fullName.trim()) &&
    formData.titles.length > 0 &&
    emailReady &&
    formData.specialties.length > 0 &&
    Boolean(formData.siteName.trim()) &&
    Boolean(formData.siteAddress.trim()) &&
    validatePhoneNumber(formData.phoneNumber) &&
    !loading

  if (success) {
    return (
      <div className="text-center space-y-2">
        <div className="text-green-600 text-3xl">✓</div>
        <h3 className="font-semibold text-green-700">Application Submitted</h3>
        <p className="text-xs text-gray-600">Our team received your details and will reach out with next steps.</p>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="p-3 rounded-lg bg-red-50 border border-red-200">
          <p className="text-xs text-red-800">{error}</p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Full Name *</label>
          <input
            type="text"
            name="fullName"
            value={formData.fullName}
            onChange={handleChange}
            required
            placeholder="John Smith"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Professional Titles (NP, PA, MD, DO, etc.) *</label>
          <Popover open={titlePopoverOpen} onOpenChange={setTitlePopoverOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                role="combobox"
                aria-expanded={titlePopoverOpen}
                className="w-full justify-between border-dashed"
              >
                <span className="flex items-center gap-2 text-sm text-gray-700">
                  {formData.titles.length
                    ? `${formData.titles.length} title${formData.titles.length === 1 ? "" : "s"} selected`
                    : "Select titles"}
                </span>
                <ChevronsUpDown className="h-4 w-4 text-gray-400" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[240px] p-0" align="start" side="bottom">
              <Command shouldFilter={false}>
                <CommandInput
                  placeholder="Search or add titles..."
                  value={titleQuery}
                  onValueChange={setTitleQuery}
                  onKeyDown={handleTitleInputKeyDown}
                  className="text-sm"
                />
                <CommandList className="max-h-[300px] overflow-y-auto">
                  <CommandEmpty>
                    <div className="px-3 py-2 text-sm text-gray-600">
                      <p>No titles found.</p>
                      {titleQuery.trim() && (
                        <button
                          type="button"
                          className="mt-2 text-xs font-medium text-indigo-600 hover:underline"
                          onMouseDown={(event) => {
                            event.preventDefault()
                            handleAddCustomTitle(titleQuery)
                          }}
                        >
                          Add &quot;{titleQuery.trim()}&quot;
                        </button>
                      )}
                    </div>
                  </CommandEmpty>
                  <CommandGroup>
                    {TITLE_OPTIONS.filter((option) =>
                      option.label.toLowerCase().includes(titleQuery.toLowerCase())
                    ).map((option) => {
                      const isSelected = formData.titles.some(
                        (title) => title.toLowerCase() === option.value.toLowerCase()
                      )

                      return (
                        <CommandItem
                          key={option.value}
                          onSelect={() => {
                            toggleTitle(option.value)
                            setTitleQuery("")
                          }}
                          className="flex items-center justify-between text-sm"
                        >
                          <span>{option.label}</span>
                          {isSelected && <Check className="h-4 w-4" />}
                        </CommandItem>
                      )
                    })}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          <div className="mt-2 flex flex-wrap gap-2">
            {formData.titles.length ? (
              formData.titles.map((title) => (
                <span
                  key={title}
                  className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-medium text-indigo-700 ring-1 ring-inset ring-indigo-200"
                >
                  {title}
                  <button
                    type="button"
                    onClick={() => removeTitle(title)}
                    className="inline-flex h-4 w-4 items-center justify-center rounded-full text-indigo-500 hover:text-indigo-700"
                    aria-label={`Remove ${title}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))
            ) : (
              <span className="text-[10px] text-gray-500">Select every title that applies.</span>
            )}
          </div>
          <small className="mt-1 block text-[10px] text-gray-500">
            Use the search to pick titles. Press Enter to add custom ones. You can add up to {MAX_TITLES} titles.
          </small>
          {formData.titles.length >= MAX_TITLES && (
            <small className="mt-1 block text-[10px] text-red-600">Maximum number of titles selected.</small>
          )}
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Email *</label>
        <div className="relative">
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            onBlur={() => setEmailTouched(true)}
            required
            placeholder="john@example.com"
            className={cn(
              "w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500",
              emailTouched && !emailMessage && "border-emerald-500 focus:ring-emerald-500",
              emailTouched && Boolean(emailMessage) && "border-red-500 focus:ring-red-500"
            )}
          />
          {emailTouched && !emailMessage && (
            <CheckCircle2
              className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-emerald-500"
            />
          )}
          {emailTouched && Boolean(emailMessage) && (
            <AlertCircle
              className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-red-500"
            />
          )}
        </div>
        <small className="block mt-1 text-[10px] text-gray-500">We&apos;ll use this email to contact you about your application</small>
        {emailTouched && emailMessage && (
          <small className="mt-1 block text-[10px] text-red-600" aria-live="polite">
            {emailMessage}
          </small>
        )}
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Phone Number *</label>
        <div className={cn(
          "rounded-lg",
          phoneError && "ring-2 ring-red-500"
        )}>
          <ShadcnPhoneInput
            value={formData.phoneNumber || undefined}
            onChange={handlePhoneChange}
            defaultCountry="US"
            international={false}
            addInternationalOption={false}
            name="phoneNumber"
            required
            className="w-full"
          />
        </div>
        {phoneError && (
          <small className="mt-1 text-[10px] text-red-600 flex items-center gap-1" aria-live="polite">
            <AlertCircle className="h-3 w-3" />
            {phoneError}
          </small>
        )}
        {!phoneError && formData.phoneNumber && validatePhoneNumber(formData.phoneNumber) && (
          <small className="mt-1 text-[10px] text-emerald-600 flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3" />
            Phone number looks good
          </small>
        )}
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Specialties *</label>
        <Popover open={specialtyPopoverOpen} onOpenChange={setSpecialtyPopoverOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              role="combobox"
              aria-expanded={specialtyPopoverOpen}
              className="w-full justify-between border-dashed"
              disabled={loadingSpecialties}
            >
              <span className="flex items-center gap-2 text-sm text-gray-700">
                {loadingSpecialties
                  ? "Loading specialties..."
                  : formData.specialties.length
                    ? `${formData.specialties.length} selected`
                    : "Select specialties"}
              </span>
              <ChevronsUpDown className="h-4 w-4 text-gray-400" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[260px] p-0" align="start" side="bottom">
            <Command shouldFilter={false}>
              <CommandInput
                placeholder="Search specialties..."
                value={specialtyQuery}
                onValueChange={setSpecialtyQuery}
                className="text-sm"
              />
              <CommandList className="max-h-[300px] overflow-y-auto">
                <CommandEmpty>No specialty found.</CommandEmpty>
                <CommandGroup>
                  {specialties
                    .filter((spec) => spec.name.toLowerCase().includes(specialtyQuery.toLowerCase()))
                    .map((spec) => {
                      const isSelected = formData.specialties.includes(spec.name)
                      return (
                        <CommandItem
                          key={spec.id}
                          onSelect={() => {
                            setFormData((prev) => {
                              const exists = prev.specialties.includes(spec.name)
                              return {
                                ...prev,
                                specialties: exists
                                  ? prev.specialties.filter((s) => s !== spec.name)
                                  : [...prev.specialties, spec.name],
                              }
                            })
                          }}
                          className="flex items-center justify-between text-sm"
                        >
                          <span>{spec.name}</span>
                          {isSelected && <Check className="h-4 w-4" />}
                        </CommandItem>
                      )
                    })}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        <div className="mt-2 flex flex-wrap gap-2">
          {formData.specialties.length ? (
            formData.specialties.map((spec) => (
              <span
                key={spec}
                className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-medium text-indigo-700 ring-1 ring-inset ring-indigo-200"
              >
                {spec}
                <button
                  type="button"
                  onClick={() =>
                    setFormData((prev) => ({
                      ...prev,
                      specialties: prev.specialties.filter((s) => s !== spec),
                    }))
                  }
                  className="inline-flex h-4 w-4 items-center justify-center rounded-full text-indigo-500 hover:text-indigo-700"
                  aria-label={`Remove ${spec}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))
          ) : (
            <span className="text-[10px] text-gray-500">Select all specialties that apply.</span>
          )}
        </div>
      </div>

      {/* Site Information */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Site Name *</label>
          <input
            type="text"
            name="siteName"
            value={formData.siteName}
            onChange={handleChange}
            required
            placeholder="Enter your clinic, hospital, or practice name"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Site Address *</label>
          <input
            type="text"
            name="siteAddress"
            value={formData.siteAddress}
            onChange={handleChange}
            required
            placeholder="Enter the full site address"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
        </div>
      </div>
      <small className="block -mt-2 text-[10px] text-gray-500">
        Please note we work with client-facing offices. We can&apos;t accept your address if it&apos;s a post office.
      </small>

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-2">Have You Precepted Before? *</label>
        <div className="space-y-2">
          {[
            { value: "yes", label: "Yes" },
            { value: "no", label: "No" },
          ].map((option) => (
            <label key={option.value} className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="preceptedBefore"
                value={option.value}
                checked={formData.preceptedBefore === option.value}
                onChange={handleChange}
                required={formData.preceptedBefore === ""}
                className="w-3 h-3 text-indigo-600"
              />
              <span className="text-sm text-gray-700">{option.label}</span>
            </label>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Preferred Schedule (Optional)</label>
        <small className="block text-[10px] text-gray-500 mb-2">
          Describe your availability in any format, e.g. &quot;Mon–Fri 9AM–5PM&quot; or &quot;Tue &amp; Thu afternoons&quot;.
        </small>
        <textarea
          name="scheduleNotes"
          value={formData.scheduleNotes}
          onChange={handleChange}
          rows={3}
          placeholder="e.g. M 9:00AM - 4:00PM, Fri 1:00PM - 5:00PM"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-2">Patient Population (Ages) (Optional)</label>
        <div className="space-y-2">
          {PATIENT_POPULATIONS.map((pop) => (
            <label key={pop} className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                value={pop}
                checked={formData.patientPopulation.includes(pop)}
                onChange={handleCheckboxChange}
                className="w-3 h-3 text-indigo-600 rounded"
              />
              <span className="text-sm text-gray-700">{pop}</span>
            </label>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Anything you want to tell us? (Optional)</label>
        <textarea
          name="additionalNotes"
          value={formData.additionalNotes}
          onChange={handleChange}
          rows={3}
          placeholder="Share any preferences, questions, or program details we should know."
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
      </div>

      <Button
        type="submit"
        disabled={loading || !isFormComplete}
        className={cn(
          "w-full bg-gradient-to-r from-indigo-600 to-cyan-500 hover:from-indigo-700 hover:to-cyan-600 text-white font-medium py-2 rounded-lg text-sm transition-all",
          (loading || !isFormComplete) && "opacity-70 cursor-not-allowed"
        )}
      >
        {loading ? "Submitting..." : "Submit"}
      </Button>

      <small className="block text-center text-[10px] text-gray-500">The team will review your application.</small>
    </form>
  )
}
